import React from "react";
import Axios from "axios";
import { create } from "apisauce";

import {
  pawnshop_reports_reader_host,
  pawnshop_reports_reader_host_v3,
  pawnshop_reports_reader_host_v4,
  pawnshop_reports_settings_host,
  crdm_host,
} from "../constants/hosts/hosts";

import { toastr } from "react-redux-toastr";
import { Button, Table } from "react-bootstrap";
import { openModal } from "../store/actions/ModalActions";

// Function to open IndexedDB database
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open("responseCache", 1);

    request.onerror = (event) => {
      reject("IndexedDB error: " + event.target.errorCode);
    };

    request.onsuccess = (event) => {
      resolve(event.target.result);
    };

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      const store = db.createObjectStore("responses", { keyPath: "url" });
      store.createIndex("url", "url", { unique: true });
    };
  });
};

// Function to add response to IndexedDB
const addResponseToDB = async (response) => {
  const db = await openDatabase();
  const transaction = db.transaction(["responses"], "readwrite");
  const store = transaction.objectStore("responses");
  const url = response.config.url; // Get the URL from the response config
  store.put({ url, response }); // Save URL along with the response
};

// Function to retrieve response from IndexedDB
const getCachedResponseFromDB = async (url) => {
  const db = await openDatabase();
  const transaction = db.transaction(["responses"], "readonly");
  const store = transaction.objectStore("responses");
  const request = store.get(url);
  return new Promise((resolve, reject) => {
    request.onsuccess = (event) => {
      const cachedResponse = event.target.result;
      if (cachedResponse) {
        // Return only the response part, excluding the URL
        resolve(cachedResponse.response);
      } else {
        resolve(null); // No cached response found
      }
    };
    request.onerror = (event) => {
      reject("Error retrieving data from IndexedDB");
    };
  });
};

// Function to add caching interceptors to axios instance
const addCachingInterceptors = (axiosInstance) => {
  axiosInstance.interceptors.request.use(async (config) => {
    // Check if a cached response exists in IndexedDB
    const cachedResponse = await getCachedResponseFromDB(config.url);
    if (cachedResponse) {
      // If a cached response exists, return it immediately
      return Promise.resolve(cachedResponse);
    }
    return config;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      // Check if response needs to be cached
      if (response.config.method === "GET" || response.config.method === "POST") {
        // Cache the response in IndexedDB for future identical requests
        addResponseToDB(response);
      }
      return response;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

//axios instances
const createAxiosInstance = (baseURL, headers = {}) => {
  return Axios.create({
    baseURL,
    headers: {
      accept: "application/json",
      ...headers,
    },
  });
};
//v4
const premise_pawnshop_reports_reader_service_v4 = createAxiosInstance(
  pawnshop_reports_reader_host_v4
);
const premise_pawnshop_reports_reader_service_v3 = createAxiosInstance(
  pawnshop_reports_reader_host_v3
);
const premise_pawnshop_reports_reader_service = createAxiosInstance(
  pawnshop_reports_reader_host
);
const premise_pawnshop_settings_reader_service = createAxiosInstance(
  pawnshop_reports_settings_host
);
const premise_crdm_service = createAxiosInstance(crdm_host);

// Add caching interceptors to axios instances
addCachingInterceptors(premise_pawnshop_reports_reader_service_v3);
addCachingInterceptors(premise_pawnshop_reports_reader_service_v4);
addCachingInterceptors(premise_pawnshop_reports_reader_service);
addCachingInterceptors(premise_pawnshop_settings_reader_service);
addCachingInterceptors(premise_crdm_service);

// Create service instances using apisauce
const createService = (axiosInstance) => {
  return create({
    axiosInstance,
    timeout: 1000,
    timeoutErrorMessage: "Error time out",
  });
};

export const pawnshop_reports_reader_service = createService(
  premise_pawnshop_reports_reader_service
);

export const pawnshop_reports_reader_service_v3 = createService(
  premise_pawnshop_reports_reader_service_v3
);
//v4
export const pawnshop_reports_reader_service_v4 = createService(
  premise_pawnshop_reports_reader_service_v4
);
export const pawnshop_reports_settings_service = createService(
  premise_pawnshop_settings_reader_service
);
export const crdm_service = createService(premise_crdm_service);

crdm_service.axiosInstance.interceptors.request.use(
  async (config) => {
    // Extract the URL from the config
    const url = config.url;
    
    // Check if a cached response exists for the request URL
    const cachedResponse = await getCachedResponseFromDB(url);
    if (cachedResponse) {
      // If a cached response exists, return it immediately
      return Promise.resolve(cachedResponse);
    }

    // Set Authorization header
    config.headers.Authorization = `Bearer ${process.env.REACT_APP_CRDM_SERVICE_DOMAIN_API_KEY}`;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

crdm_service.axiosInstance.interceptors.response.use(
  (response) => {
    // Check if response needs to be cached
    if (response.config.method === "GET" || response.config.method === "POST") {
      // Cache the response in IndexedDB for future identical requests
      addResponseToDB(response.config.url, response);
    }
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const renderContent = (responses) => {
  return (
    <Table responsive size="sm">
      <thead>
        <tr>
          <th>Timestamp</th>
          <th>Origin</th>
          <th>Status</th>
          <th>Response</th>
        </tr>
      </thead>
      <tbody>
        {responses &&
          responses.reverse().map((response, idx) => {
            return (
              <tr key={idx}>
                <td>
                  <small>{response.timestamp}</small>
                </td>
                <td className="w-20">
                  <small>{JSON.stringify(response.data.config.url)}</small>
                </td>
                <td>
                  <small>{response.data.status}</small>
                </td>
                <td>
                  <small>{JSON.stringify(response.data.data)}</small>
                </td>
              </tr>
            );
          })}
      </tbody>
    </Table>
  );
};

export const generateViewer = (errorResponses) => {
  return (
    <div>
      There was an error processing the request
      <Button
        className="ml-3"
        variant="info"
        onClick={() => {
          openModal({
            custom: true,
            title: "Error Details",
            size: "lg",
            currentComponent: renderContent(errorResponses),
          });
        }}
        size="sm"
      >
        View Details
      </Button>
    </div>
  );
};

let errorResponses = [];

const addListener = (service) => {
  var options = {
    icon: "warning",
    status: "error",
    component: null,
    position: "top-right",
    width: "400px",
  };
  service.addMonitor((response) => {
    options = { ...options, component: () => generateViewer(errorResponses) }; // Wrap generateViewer with a function

    if (response.problem !== null) {
      var currentDate = new Date();

      errorResponses.push({
        timestamp: currentDate.toString(),
        data: response,
      });
    }

    switch (response.problem) {
      case "CLIENT_ERROR":
        toastr.light(
          `Failure on processing the request error code: ${response.status}`,
          options
        );
        break;

      case "SERVER_ERROR":
        toastr.warning( "We encountered issue connecting with our servers", options);
        break;

      case "CANCEL_ERROR":
        toastr.error(response.problem, options);
        break;

      case "TIMEOUT_ERROR":
        toastr.error(response.problem, options);
        break;

      case "CONNECTION_ERROR":
        toastr.warning(
          "We encountered issue connecting with our servers",
          options
        );
        break;

      case "NETWORK_ERROR":
        toastr.warning(
          "We encountered issue connecting with our servers",
          options
        );
        break;

      default:
        break;
    }
  });
};

addListener(pawnshop_reports_reader_service_v3);
addListener(pawnshop_reports_reader_service_v4);
addListener(pawnshop_reports_reader_service);
addListener(pawnshop_reports_settings_service);
addListener(crdm_service);
